// src/components/CustomDropdown.jsx
import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

const CustomDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  className = "",
  disabled = false,
  icon,
  error = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        className={`
          w-full px-4 py-2.5 bg-transparent border rounded-full text-left
          flex justify-between items-center text-sm transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-purple-500/50
          ${error ? "border-red-500" : "border-gray-600"}
          ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-white/60"}
          text-[#EDEDEDE5] ${className}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="flex items-center gap-2 truncate">
          {icon && <span className="text-gray-400">{icon}</span>}
          <span className={value ? "text-[#EDEDEDE5]" : "text-gray-500"}>
            {displayLabel}
          </span>
        </span>

        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 text-gray-400
            ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <ul
          className="absolute z-50 w-full mt-2 bg-[#0a1733] border border-[#5A4479] 
                     rounded-lg shadow-2xl max-h-60 overflow-auto scrollbar-thin 
                     scrollbar-thumb-purple-600 scrollbar-track-transparent"
          role="listbox"
        >
          {options.length > 0 ? (
            options.map((option) => (
              <li
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`
                  px-4 py-2.5 text-sm cursor-pointer transition-colors duration-150
                  hover:bg-[#2A2A2A] text-[#CCCCCC]
                  ${value === option.value ? "bg-[#1a1a3a] text-white font-medium" : ""}
                `}
                role="option"
                aria-selected={value === option.value}
              >
                <span className="flex items-center gap-2">
                  {option.icon && option.icon}
                  {option.label}
                </span>
              </li>
            ))
          ) : (
            <li className="px-4 py-3 text-gray-500 text-sm italic text-center">
              No options available
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default CustomDropdown;